import React from "react";
import { Volume2, BookOpen } from "lucide-react";
import { renderFurigana } from "@/lib/japaneseHelpers";
import type { QuestionScenario } from "@/types/exam";

interface ScenarioBlockProps {
  scenario: QuestionScenario;
  questionCount: number;
  audioElement?: React.ReactNode;
  children: React.ReactNode;
}

export function ScenarioBlock({
  scenario,
  questionCount,
  audioElement,
  children,
}: ScenarioBlockProps) {
  return (
    <div className="mt-4 mb-8">
      <div className="rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
        {/* Scenario header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-white border-b border-stone-200 select-none">
          <div className="flex items-center gap-2.5">
            {scenario.audioUrl ? (
              <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                <Volume2 size={13} className="text-purple-600" />
              </div>
            ) : (
              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <BookOpen size={13} className="text-blue-600" />
              </div>
            )}
            <span className="text-xs font-bold uppercase tracking-widest text-stone-600 font-sans">
              {scenario.audioUrl ? "聴解問題 · Listening Passage" : "読解本文 · Reading Passage"}
            </span>
          </div>
          <span className="text-[10px] bg-stone-100 text-stone-500 px-2.5 py-1 rounded-full font-mono font-bold border border-stone-200 shrink-0">
            {questionCount}問
          </span>
        </div>

        {/* Scenario content wrapper (for blur overlay in parent if needed) */}
        <div className="relative">
          {/* Passage body */}
          <div className="bg-gradient-to-b from-[#fdfbf7] to-[#f9f6ef] p-4 sm:p-5 md:p-6 space-y-5">
            {audioElement && (
              <div className="relative z-20 mb-4">
                {audioElement}
              </div>
            )}

            {scenario.instructionsText && (
              <div className="text-stone-900 leading-loose text-[1.05rem] whitespace-pre-wrap">
                {renderFurigana(scenario.instructionsText)}
              </div>
            )}

            {scenario.imageUrl && (
              <img
                src={scenario.imageUrl}
                alt=""
                className="max-h-[550px] w-full max-w-3xl object-contain pointer-events-none mx-auto"
              />
            )}
          </div>

          {/* Questions section */}
          <div className="border-t-2 border-dashed border-stone-200 bg-[#f5f4f1]/40 px-4 sm:px-5 md:px-6 py-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 font-sans select-none">問題 / Questions</p>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
